class VfProductVideos extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.visible = new Set();
    this.boundVideos = new WeakSet();
    this.dialog = this.querySelector('dialog');
    this.media = this.querySelector('.vfpv-media');
    this.select = this.querySelector('select');
    this.mobile = matchMedia('(max-width: 749px)');
    this.mobile.addEventListener('change', () => this.syncControls(), options);
    document.addEventListener('fullscreenchange', () => this.syncControls(), options);
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= .4) {
          this.visible.add(entry.target);
          this.preview(entry.target);
        } else {
          this.visible.delete(entry.target);
          entry.target.querySelector('video')?.pause();
        }
      }
    }, { threshold: [0, .4] });
    this.querySelectorAll('[data-video-open]').forEach(el => this.observer.observe(el));
    this.addEventListener('click', event => {
      const trigger = event.target.closest('[data-video-open]');
      if (trigger) this.open(trigger);
      if (event.target.closest('[data-video-close]') || event.target === this.dialog) this.dialog.close();
      const choice = event.target.closest('[data-video-variant]');
      if (choice && !choice.disabled) { this.select.value = choice.dataset.videoVariant; this.variant(); }
      const video = this.media.querySelector('video');
      if (video && event.target.closest('[data-video-play]')) {
        this.userPaused = !video.paused;
        if (this.userPaused) video.pause();
        else this.playActive(video);
      }
      if (video && event.target.closest('[data-video-sound]')) video.muted = !video.muted;
      if (video && event.target.closest('[data-video-fullscreen]')) this.fullscreen(video);
    }, options);
    this.select.addEventListener('change', () => this.variant(), options);
    this.querySelector('form').addEventListener('submit', async event => {
      const option = this.select.selectedOptions[0];
      if (!option || option.disabled || this.submitting) { event.preventDefault(); return; }
      const drawer = document.querySelector('vf-cart-drawer');
      if (!drawer || typeof drawer.add !== 'function') return;
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      data.delete('return_to');
      this.submitting = true;
      const buy = this.querySelector('.vfpv-buy');
      buy.disabled = true;
      buy.setAttribute('aria-busy', 'true');
      await new Promise(resolve => {
        this.dialog.addEventListener('close', resolve, { once: true });
        this.dialog.close();
      });
      this.querySelectorAll('video').forEach(video => video.pause());
      try {
        await drawer.add(data);
      } catch {
        // The shared drawer presents the request error and retry state.
      } finally {
        this.submitting = false;
        buy.removeAttribute('aria-busy');
        this.variant();
      }
    }, options);
    window.addEventListener('pageshow', () => { this.submitting = false; this.variant(); }, options);
    this.dialog.addEventListener('close', () => this.close(), options);
    window.addEventListener('popstate', () => {
      this.historyClosing = false;
      this.restoreFromHistory();
    }, options);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.querySelectorAll('video').forEach(v => v.pause());
      else if (!this.dialog.open) this.visible.forEach(el => this.preview(el));
      else if (!this.userPaused) this.media.querySelector('video')?.play().catch(() => {});
    }, options);
    this.motion.addEventListener('change', () => {
      if (this.motion.matches) this.querySelectorAll('.vfpv-open video').forEach(v => v.pause());
      else this.visible.forEach(el => this.preview(el));
    }, options);
    this.restoreFromHistory();
  }
  ensure(trigger) {
    let video = trigger.querySelector('video');
    if (!video) {
      trigger.append(trigger.querySelector('template').content.cloneNode(true));
      video = trigger.querySelector('video');
    }
    if (!this.boundVideos.has(video)) {
      this.boundVideos.add(video);
      video.addEventListener('loadedmetadata', () => {
        if (this.media.contains(video) && video.videoHeight) this.dialog.style.setProperty('--vfpv-ratio', String(video.videoWidth / video.videoHeight));
      }, { once: true, signal: this.abort.signal });
      for (const type of ['play', 'pause', 'volumechange', 'webkitendfullscreen']) {
        video.addEventListener(type, () => {
          if (this.media.contains(video)) this.syncControls();
        }, { signal: this.abort.signal });
      }
      video.tabIndex = -1;
      video.setAttribute('aria-hidden', 'true');
    }
    return video;
  }
  preview(trigger) {
    if (this.dialog.open || document.hidden || this.motion.matches) return;
    const video = this.ensure(trigger);
    video.controls = false;
    video.muted = true;
    video.play().catch(() => {});
  }
  open(trigger, fromHistory = false) {
    if (this.dialog.open || this.historyClosing) return;
    this.opener = trigger;
    this.trigger = trigger;
    this.querySelectorAll('video').forEach(v => v.pause());
    const video = this.ensure(trigger);
    this.dialog.style.setProperty("--vfpv-ratio", video.videoWidth && video.videoHeight ? String(video.videoWidth / video.videoHeight) : this.trigger.dataset.aspectRatio || "0.5625");
    this.media.append(video);
    const pageVariant = new URL(location.href).searchParams.get('variant');
    if ([...this.select.options].some(o => o.value === pageVariant && !o.disabled)) this.select.value = pageVariant;
    this.variant();
    this.userPaused = false;
    this.oldOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.dialog.showModal();
    if (!fromHistory) {
      const index = [...this.querySelectorAll('[data-video-open]')].indexOf(trigger);
      history.pushState({ ...history.state, vfProductVideo: { id: this.dialog.id, index } }, '', location.href);
    }
    this.dialog.scrollTop = 0;
    video.controls = !this.mobile.matches;
    video.removeAttribute('aria-hidden');
    video.removeAttribute('tabindex');
    video.muted = false;
    this.syncControls();
    this.playActive(video);
  }
  playActive(video) {
    video.play().catch(() => {
      if (!this.dialog.open || !this.media.contains(video) || this.userPaused) return;
      video.muted = true;
      video.play().catch(() => {});
    });
  }
  syncControls() {
    const video = this.media.querySelector('video');
    if (!video) return;
    video.controls = !this.mobile.matches || document.fullscreenElement === video || !!video.webkitDisplayingFullscreen;
    const controls = this.querySelector('.vfpv-controls');
    controls.dataset.paused = String(video.paused);
    controls.dataset.muted = String(video.muted);
    const play = controls.querySelector('[data-video-play]');
    const sound = controls.querySelector('[data-video-sound]');
    play.setAttribute('aria-label', video.paused ? play.dataset.playLabel : play.dataset.pauseLabel);
    sound.setAttribute('aria-label', video.muted ? sound.dataset.unmuteLabel : sound.dataset.muteLabel);
    controls.querySelector('[data-video-fullscreen]').hidden = !video.requestFullscreen && !video.webkitEnterFullscreen;
  }
  fullscreen(video) {
    video.controls = true;
    try {
      if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
      else if (video.requestFullscreen) video.requestFullscreen().catch(() => this.syncControls());
      else this.syncControls();
    } catch {
      this.syncControls();
    }
  }
  variant() {
    const option = this.select.selectedOptions[0];
    const buy = this.querySelector('.vfpv-buy');
    buy.disabled = this.submitting || !option || option.disabled;
    buy.textContent = buy.disabled ? 'Sold out' : 'Add to cart';
    this.querySelector('[data-video-price]').textContent = option?.dataset.price || '';
    const label = this.querySelector('[data-video-option]');
    if (label) label.textContent = option?.textContent || '';
    this.querySelectorAll('[data-video-variant]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.videoVariant === option?.value)));
    const image = this.querySelector('.vfpv-product-image');
    if (image && option?.dataset.image && image.getAttribute('src') !== option.dataset.image) {
      image.removeAttribute('srcset'); image.src = option.dataset.image;
    }
  }
  restoreFromHistory() {
    const entry = history.state?.vfProductVideo;
    if (entry?.id === this.dialog.id) {
      const trigger = this.querySelectorAll('[data-video-open]')[entry.index];
      if (trigger && !this.dialog.open) this.open(trigger, true);
    } else if (this.dialog.open) {
      this.dialog.close();
    }
  }
  close() {
    if (this.dialog.open) return;
    if (history.state?.vfProductVideo?.id === this.dialog.id) {
      if (this.submitting && this.mobile.matches) {
        const state = { ...history.state, vfMobileOverlay: true };
        delete state.vfProductVideo;
        history.replaceState(state, '', location.href);
      } else if (this.isConnected) {
        this.historyClosing = true;
        history.back();
      } else {
        const state = { ...history.state };
        delete state.vfProductVideo;
        history.replaceState(state, '', location.href);
      }
    }
    const video = this.media.querySelector('video');
    if (video && this.trigger) { video.pause(); video.muted = true; video.controls = false; video.tabIndex = -1; video.setAttribute("aria-hidden", "true"); this.trigger.append(video); }
    document.documentElement.style.overflow = this.oldOverflow ?? '';
    this.opener?.focus({ preventScroll: true });
    if (this.isConnected) this.visible.forEach(el => this.preview(el));
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    if (this.dialog?.open) { this.dialog.close(); this.close(); }
    this.querySelectorAll('video').forEach(v => v.pause());
  }
}
if (!customElements.get('vf-product-videos')) customElements.define('vf-product-videos', VfProductVideos);
